// Ruler Tool — measures distance and angle between two points.
// Shares shortcut I with Eyedropper and Color Sampler.

import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface RulerMeasurement {
  x1: number; y1: number;
  x2: number; y2: number;
  distance: number;
  angle: number; // degrees, 0 = horizontal right, increasing CCW
  deltaX: number;
  deltaY: number;
}

export class RulerTool implements Tool {
  readonly id = "ruler";
  readonly shortcut = "I";

  /** Current measurement, or null if no measurement has been made. */
  measurement: RulerMeasurement | null = null;

  private startPoint: { x: number; y: number } | null = null;
  private endPoint: { x: number; y: number } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.startPoint = { x: Math.round(p.x), y: Math.round(p.y) };
    this.endPoint = null;
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.startPoint || !ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.endPoint = { x: Math.round(p.x), y: Math.round(p.y) };
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.startPoint || !this.endPoint || !ctx.doc) return;
    const dx = this.endPoint.x - this.startPoint.x;
    const dy = this.endPoint.y - this.startPoint.y;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(-dy, dx) * (180 / Math.PI); // CCW from horizontal right

    this.measurement = {
      x1: this.startPoint.x, y1: this.startPoint.y,
      x2: this.endPoint.x, y2: this.endPoint.y,
      distance: Math.round(distance * 100) / 100,
      angle: Math.round(angle * 100) / 100,
      deltaX: dx, deltaY: dy,
    };
    this.startPoint = null;
    this.endPoint = null;
    ctx.requestRender();
  }

  /** Clear the current measurement. */
  clear(): void {
    this.measurement = null;
    this.startPoint = null;
    this.endPoint = null;
  }
}
